import React, { useEffect, useState, useSyncExternalStore } from "react";
import { TextField, Autocomplete, Box } from "@mui/material";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { GetPhotoSizeDto, PriceRangeDto } from "src/services/generated-api";
import PriceLadderDisplay from "@components/photo-size/price-ladder-display";
import { ladderForCurrency } from "src/utils/photo-size-ladder";
import { defaultPriceInCurrency } from "src/utils/photo-size-price";

interface PhotoSizeSelectorProps {
  control: Control<{ photoSizeId: string }>;
  errors: FieldErrors<{ photoSizeId: string }>;
  photoSizeId: string;
  photoSize: GetPhotoSizeDto | undefined;
  autocompleteProps: { options: GetPhotoSizeDto[] };
  setValue: (name: "photoSizeId", value: string) => void;
  getValues: (name: "photoSizeId") => string;
  currentLocaleFixed: string;
  currency: string;
}

const COARSE_POINTER = "(pointer: coarse)";
const isCoarsePointer = () =>
  window.matchMedia?.(COARSE_POINTER).matches ?? false;
const subscribeCoarsePointer = (onChange: () => void) => {
  const mq = window.matchMedia?.(COARSE_POINTER);
  mq?.addEventListener("change", onChange);
  return () => mq?.removeEventListener("change", onChange);
};

const PhotoSizeSelector: React.FC<PhotoSizeSelectorProps> = ({
  control,
  errors,
  photoSize,
  autocompleteProps,
  currentLocaleFixed,
  currency,
}) => {
  const translate = useAppTranslate();

  // The "PRIKAŽI/SKRIJ CENE" list: this currency's own ladder or none, never
  // the euro ladder relabelled (currency walk B4).
  const returnPricingLadder = (
    ps: GetPhotoSizeDto | undefined,
    cur: string
  ) => ladderForCurrency<PriceRangeDto>(ps, cur);

  const photoUrl = (
    photoSize as { previewPhoto?: { url?: string } | null } | undefined
  )?.previewPhoto?.url;

  // On a touch screen the selector is pick-only. As a type-to-search field,
  // tapping it raised the keyboard, which left the inline list a sliver of
  // screen to scroll through between the field and the keyboard — and a
  // shopper does not want to type "30x40", they want to tap it. Desktop keeps
  // type-to-search.
  const touch = useSyncExternalStore(
    subscribeCoarsePointer,
    isCoarsePointer,
    () => false
  );

  const [loadFailedUrl, setLoadFailedUrl] = useState<string | null>(null);
  useEffect(() => {
    setLoadFailedUrl(null);
  }, [photoUrl]);
  const showPreview = !!photoUrl && loadFailedUrl !== photoUrl;

  const nameMap = photoSize?.name as Record<string, string> | undefined;
  const sizeName =
    (nameMap?.[currentLocaleFixed ?? "sl"] as string) ||
    (nameMap ? Object.values(nameMap)[0] : "") ||
    "";

  return (
    <Box sx={{ mt: 1 }}>
      {/* Hero preview card — only rendered when a preview image is available */}
      {showPreview ? (
      <Box
        sx={(theme) => ({
          position: "relative",
          borderRadius: "20px",
          overflow: "hidden",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.07) 55%, rgba(255,255,255,0.05) 100%)"
            : "linear-gradient(160deg, #F8FAFC 0%, #EEF2F7 55%, #E6EBF2 100%)",
          border: "1px solid rgba(16, 155, 197, 0.14)",
          boxShadow:
            "0 1px 2px rgba(16,24,40,0.04), 0 12px 28px -10px rgba(16,24,40,0.14)",
          mb: 2,
        })}
      >
        {/* subtle corner accent */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(16,155,197,0.18) 0%, rgba(16,155,197,0) 70%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            height: { xs: 220, sm: 280 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1.5, sm: 2 },
          }}
          key={photoUrl || "no-preview"}
        >
          <Box
            component="img"
            src={photoUrl}
            alt={sizeName || "Photo size preview"}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={() => setLoadFailedUrl(photoUrl ?? null)}
            sx={{
              maxWidth: "94%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
              borderRadius: "6px",
              boxShadow:
                "0 10px 24px -10px rgba(16,24,40,0.28), 0 4px 10px -4px rgba(16,24,40,0.15)",
              transform: "rotate(-0.5deg)",
              transition: "transform 200ms ease",
              "&:hover": { transform: "rotate(0deg) scale(1.02)" },
            }}
          />

        </Box>
      </Box>
      ) : null}

      {/* Selector */}
      <Controller
        name="photoSizeId"
        control={control}
        rules={{
          required: translate("errors.required.field", {
            field: "photoSizeId",
          }),
        }}
        render={({ field }) => (
          <Autocomplete
            disablePortal
            {...autocompleteProps}
            value={
              autocompleteProps.options.find(
                (option: GetPhotoSizeDto) => option.id === field.value
              ) || null
            }
            onChange={(_, selectedOption: GetPhotoSizeDto | null) => {
              field.onChange(selectedOption?.id);
            }}
            getOptionLabel={(item: GetPhotoSizeDto) => {
              const itemName = item?.name as Record<string, string> | undefined;
              const key = currentLocaleFixed ?? "sl";
              return itemName?.[key] ? itemName[key] : "";
            }}
            fullWidth
            disableClearable={touch}
            slotProps={{ listbox: { style: { maxHeight: "50vh" } } }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  readOnly: touch,
                  inputMode: touch ? "none" : params.inputProps.inputMode,
                }}
                label={translate("order.fields.photoSize")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                margin="none"
                required
                error={!!errors.photoSizeId?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    background: "background.paper",
                  },
                }}
              />
            )}
          />
        )}
      />

      <Box sx={{ mt: 2 }}>
        <PriceLadderDisplay
          pricingLadder={returnPricingLadder(photoSize, currency) ?? undefined}
          default={defaultPriceInCurrency(photoSize, currency) ?? 0}
          currency={currency}
        />
      </Box>
    </Box>
  );
};

export default PhotoSizeSelector;
